import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Row, Col, Form, Spinner, Card, Nav, Tab, Table, Badge, Button } from 'react-bootstrap';
import { NotificationManager } from 'react-notifications';
import { FiUser, FiMail, FiPhone, FiMapPin, FiCreditCard, FiArrowLeft, FiSave, FiTrash2, FiLock, FiDollarSign } from 'react-icons/fi';
import { FaBus } from 'react-icons/fa';
import moment from 'moment';

import { AppContext } from '../AppContext';
import { tre } from '../trebus';
import SelectStop from './../components/SelectStop';

export default function User() {
    const context = useContext(AppContext);
    const navigate = useNavigate();
    const { id } = useParams();
    const [user, setUser] = useState({
        role: 'USER',
        go: {},
        come: {}
    });
    const [creating, setCreating] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [activeTab, setActiveTab] = useState('info');
    const [payments, setPayments] = useState([]);
    const [loadingPayments, setLoadingPayments] = useState(false);

    // Load user
    const load = () => {
        setIsLoading(true);
        tre.api('GET', `/users/${id}`, {
            context: context,
            success: (r) => {
                setUser(r);
                setIsLoading(false);
            },
            error: () => setIsLoading(false)
        });
    };

    // Load payments
    const loadPayments = () => {
        if (creating || !id) return;

        setLoadingPayments(true);
        tre.api('GET', `/users/${id}/payments`, {
            context: context,
            success: (response) => {
                if (response.data && Array.isArray(response.data)) {
                    const sortedPayments = [...response.data].sort((a, b) => {
                        return moment(b.created_at).valueOf() - moment(a.created_at).valueOf();
                    });
                    setPayments(sortedPayments);
                }
                setLoadingPayments(false);
            },
            error: () => setLoadingPayments(false)
        });
    };

    useEffect(() => {
        if (id === 'create') {
            setCreating(true);
            setIsLoading(false);
            setUser({
                role: 'USER',
                go: {},
                come: {}
            });
        } else {
            setCreating(false);
            load();
            if (activeTab === 'payments') {
                loadPayments();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, context]);

    useEffect(() => {
        if (activeTab === 'payments' && !creating) {
            loadPayments();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    const handleForm = (ev) => {
        const { name, value } = ev.target;
        setUser((prevUser) => ({ ...prevUser, [name]: value }));
    };

    const onChangeGo = (go) => {
        setUser((prevUser) => ({ ...prevUser, go: { id: go } }));
    };

    const onChangeCome = (come) => {
        setUser((prevUser) => ({ ...prevUser, come: { id: come } }));
    };

    const save = () => {
        setIsSaving(true);
        if (creating) {
            tre.api('POST', `/companies/${context.user.company_id}/users`, {
                context: context,
                params: user,
                success: () => {
                    NotificationManager.success('Usuario creado');
                    setIsSaving(false);
                    navigate('/users');
                },
                error: () => setIsSaving(false)
            });
        } else {
            tre.api('PUT', `/users/${user.id}`, {
                context: context,
                params: user,
                success: () => {
                    NotificationManager.success('Usuario guardado');
                    setIsSaving(false);
                },
                error: () => setIsSaving(false)
            });
        }
    };

    const remove = () => {
        if (!window.confirm('¿Estás seguro de eliminar este usuario?')) return;
        setIsDeleting(true);
        tre.api('DELETE', `/users/${user.id}`, {
            context: context,
            success: () => {
                NotificationManager.success('Usuario eliminado');
                navigate('/users');
            },
            error: () => setIsDeleting(false)
        });
    };

    const getRoleBadge = (role) => {
        switch (role) {
            case 'ADMIN':
                return <Badge bg="danger" style={{ fontSize: '12px', padding: '6px 12px' }}>Administrador</Badge>;
            case 'DRIVER':
                return <Badge bg="warning" text="dark" style={{ fontSize: '12px', padding: '6px 12px' }}>Conductor</Badge>;
            default:
                return <Badge bg="primary" style={{ fontSize: '12px', padding: '6px 12px' }}>Usuario</Badge>;
        }
    };

    // -----| Render |-----
    if (isLoading) {
        return (
            <div className="container py-4" style={{ maxWidth: '900px' }}>
                <div className="text-center py-5">
                    <Spinner animation="border" style={{ color: '#1a73e8' }} />
                    <p className="mt-3 text-muted">Cargando usuario...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="container py-4" style={{ maxWidth: '900px' }}>
            {/* Header */}
            <div className="d-flex align-items-center mb-4">
                <Button
                    variant="link"
                    onClick={() => navigate('/users')}
                    className="p-0 me-3"
                    style={{ color: '#5f6368' }}
                >
                    <FiArrowLeft size={24} />
                </Button>
                <div className="flex-grow-1">
                    <h4 className="mb-0" style={{ color: '#202124', fontWeight: 500 }}>
                        <FiUser className="me-2" style={{ color: '#1a73e8' }} />
                        {creating ? 'Nuevo Usuario' : 'Editar Usuario'}
                    </h4>
                    {!creating && user.name && (
                        <small className="text-muted">
                            {user.surname ? `${user.name} ${user.surname}` : user.name}
                        </small>
                    )}
                </div>
                {!creating && getRoleBadge(user.role)}
            </div>

            {/* Tabs */}
            <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
                <Nav variant="pills" className="mb-4 gap-2">
                    <Nav.Item>
                        <Nav.Link
                            eventKey="info"
                            style={{
                                borderRadius: '20px',
                                padding: '8px 20px',
                                backgroundColor: activeTab === 'info' ? '#1a73e8' : '#f1f3f4',
                                color: activeTab === 'info' ? 'white' : '#5f6368',
                                fontWeight: 500
                            }}
                        >
                            <FiUser className="me-2" />
                            Información
                        </Nav.Link>
                    </Nav.Item>
                    {!creating && (
                        <Nav.Item>
                            <Nav.Link
                                eventKey="payments"
                                style={{
                                    borderRadius: '20px',
                                    padding: '8px 20px',
                                    backgroundColor: activeTab === 'payments' ? '#1a73e8' : '#f1f3f4',
                                    color: activeTab === 'payments' ? 'white' : '#5f6368',
                                    fontWeight: 500
                                }}
                            >
                                <FiDollarSign className="me-2" />
                                Pagos
                                {payments.length > 0 && (
                                    <Badge bg="light" text="dark" className="ms-2">{payments.length}</Badge>
                                )}
                            </Nav.Link>
                        </Nav.Item>
                    )}
                </Nav>

                <Tab.Content>
                    {/* Info Tab */}
                    <Tab.Pane eventKey="info">
                        {/* Access Info Card */}
                        <Card className="mb-4">
                            <Card.Header className="py-3" style={{ backgroundColor: '#f8f9fa' }}>
                                <div className="d-flex align-items-center">
                                    <div
                                        className="d-flex align-items-center justify-content-center rounded-circle me-3"
                                        style={{
                                            width: '40px',
                                            height: '40px',
                                            backgroundColor: '#e8f0fe',
                                            color: '#1a73e8'
                                        }}
                                    >
                                        <FiLock size={18} />
                                    </div>
                                    <div>
                                        <h6 className="mb-0" style={{ color: '#202124', fontWeight: 500 }}>Información de Acceso</h6>
                                        <small className="text-muted">Credenciales y rol del usuario</small>
                                    </div>
                                </div>
                            </Card.Header>
                            <Card.Body className="p-4">
                                <Row className="g-3">
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label style={{ fontWeight: 500, color: '#202124' }}>Login</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="login"
                                                placeholder="Nombre de usuario"
                                                value={user.login || ''}
                                                onChange={handleForm}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label style={{ fontWeight: 500, color: '#202124' }}>Contraseña</Form.Label>
                                            <Form.Control
                                                type="password"
                                                name="password"
                                                placeholder="••••••••"
                                                value={user.password || ''}
                                                onChange={handleForm}
                                            />
                                            {!creating && (
                                                <Form.Text className="text-muted">
                                                    Dejar vacío para no cambiar
                                                </Form.Text>
                                            )}
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label style={{ fontWeight: 500, color: '#202124' }}>Tipo de Usuario</Form.Label>
                                            <Form.Select
                                                value={user.role || 'USER'}
                                                onChange={(e) => setUser({ ...user, role: e.target.value })}
                                            >
                                                <option value="USER">Usuario</option>
                                                <option value="DRIVER">Conductor</option>
                                                <option value="ADMIN">Administrador</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* Personal Info Card */}
                        <Card className="mb-4">
                            <Card.Header className="py-3" style={{ backgroundColor: '#f8f9fa' }}>
                                <div className="d-flex align-items-center">
                                    <div
                                        className="d-flex align-items-center justify-content-center rounded-circle me-3"
                                        style={{
                                            width: '40px',
                                            height: '40px',
                                            backgroundColor: '#e6f4ea',
                                            color: '#34a853'
                                        }}
                                    >
                                        <FiUser size={18} />
                                    </div>
                                    <div>
                                        <h6 className="mb-0" style={{ color: '#202124', fontWeight: 500 }}>Información Personal</h6>
                                        <small className="text-muted">Datos de contacto</small>
                                    </div>
                                </div>
                            </Card.Header>
                            <Card.Body className="p-4">
                                <Row className="g-3">
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label style={{ fontWeight: 500, color: '#202124' }}>
                                                <FiUser className="me-1" /> Nombre
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="name"
                                                placeholder="Nombre"
                                                value={user.name || ''}
                                                onChange={handleForm}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={8}>
                                        <Form.Group>
                                            <Form.Label style={{ fontWeight: 500, color: '#202124' }}>Apellidos</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="surname"
                                                placeholder="Apellidos"
                                                value={user.surname || ''}
                                                onChange={handleForm}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={12}>
                                        <Form.Group>
                                            <Form.Label style={{ fontWeight: 500, color: '#202124' }}>
                                                <FiMapPin className="me-1" /> Dirección
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="address"
                                                placeholder="Dirección completa"
                                                value={user.address || ''}
                                                onChange={handleForm}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label style={{ fontWeight: 500, color: '#202124' }}>
                                                <FiPhone className="me-1" /> Móvil
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="mobile"
                                                placeholder="Teléfono móvil"
                                                value={user.mobile || ''}
                                                onChange={handleForm}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={5}>
                                        <Form.Group>
                                            <Form.Label style={{ fontWeight: 500, color: '#202124' }}>
                                                <FiMail className="me-1" /> E-mail
                                            </Form.Label>
                                            <Form.Control
                                                type="email"
                                                name="email"
                                                placeholder="correo@ejemplo.com"
                                                value={user.email || ''}
                                                onChange={handleForm}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label style={{ fontWeight: 500, color: '#202124' }}>
                                                <FiCreditCard className="me-1" /> DNI
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="dni"
                                                placeholder="12345678A"
                                                value={user.dni || ''}
                                                onChange={handleForm}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* Default Stops Card */}
                        <Card className="mb-4">
                            <Card.Header className="py-3" style={{ backgroundColor: '#f8f9fa' }}>
                                <div className="d-flex align-items-center">
                                    <div
                                        className="d-flex align-items-center justify-content-center rounded-circle me-3"
                                        style={{
                                            width: '40px',
                                            height: '40px',
                                            backgroundColor: '#fef7e0',
                                            color: '#f9ab00'
                                        }}
                                    >
                                        <FaBus size={18} />
                                    </div>
                                    <div>
                                        <h6 className="mb-0" style={{ color: '#202124', fontWeight: 500 }}>Paradas Predeterminadas</h6>
                                        <small className="text-muted">Paradas por defecto para reservas</small>
                                    </div>
                                </div>
                            </Card.Header>
                            <Card.Body className="p-4">
                                <Row className="g-3">
                                    <Col md={6}>
                                        <SelectStop
                                            caption="Parada de Ida"
                                            value={user.go ? user.go.id : ''}
                                            onChange={onChangeGo}
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <SelectStop
                                            caption="Parada de Vuelta"
                                            value={user.come ? user.come.id : ''}
                                            onChange={onChangeCome}
                                        />
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* Actions */}
                        {!user.deleted && (
                            <div className="d-flex justify-content-between">
                                <Button
                                    variant="light"
                                    onClick={() => navigate('/users')}
                                    style={{ padding: '10px 24px' }}
                                >
                                    Cancelar
                                </Button>
                                <div className="d-flex gap-2">
                                    {!creating && (
                                        <Button
                                            variant="outline-danger"
                                            onClick={remove}
                                            disabled={isDeleting}
                                            style={{ padding: '10px 24px' }}
                                        >
                                            {isDeleting ? <Spinner size="sm" className="me-2" /> : <FiTrash2 className="me-2" />}
                                            Eliminar
                                        </Button>
                                    )}
                                    <Button
                                        variant="primary"
                                        onClick={save}
                                        disabled={isSaving}
                                        style={{ padding: '10px 24px' }}
                                    >
                                        {isSaving ? <Spinner size="sm" className="me-2" /> : <FiSave className="me-2" />}
                                        {creating ? 'Crear Usuario' : 'Guardar Cambios'}
                                    </Button>
                                </div>
                            </div>
                        )}
                    </Tab.Pane>

                    {/* Payments Tab */}
                    <Tab.Pane eventKey="payments">
                        <Card>
                            <Card.Header className="py-3" style={{ backgroundColor: '#f8f9fa' }}>
                                <div className="d-flex align-items-center">
                                    <div
                                        className="d-flex align-items-center justify-content-center rounded-circle me-3"
                                        style={{
                                            width: '40px',
                                            height: '40px',
                                            backgroundColor: '#e8f0fe',
                                            color: '#1a73e8'
                                        }}
                                    >
                                        <FiDollarSign size={18} />
                                    </div>
                                    <div>
                                        <h6 className="mb-0" style={{ color: '#202124', fontWeight: 500 }}>
                                            Historial de Pagos
                                            <Badge bg="secondary" className="ms-2">{payments.length}</Badge>
                                        </h6>
                                        <small className="text-muted">Todos los pagos del usuario</small>
                                    </div>
                                </div>
                            </Card.Header>
                            <Card.Body className="p-0">
                                {loadingPayments ? (
                                    <div className="text-center py-5">
                                        <Spinner animation="border" style={{ color: '#1a73e8' }} />
                                        <p className="mt-3 text-muted">Cargando pagos...</p>
                                    </div>
                                ) : payments.length === 0 ? (
                                    <div className="text-center py-5">
                                        <FiDollarSign size={48} className="mb-3" style={{ color: '#dadce0' }} />
                                        <p className="text-muted">No hay pagos registrados</p>
                                    </div>
                                ) : (
                                    <Table hover responsive className="mb-0">
                                        <thead style={{ backgroundColor: '#f8f9fa' }}>
                                            <tr>
                                                <th className="py-3 px-3" style={{ fontWeight: 500, color: '#5f6368', fontSize: '13px' }}>FECHA</th>
                                                <th className="py-3" style={{ fontWeight: 500, color: '#5f6368', fontSize: '13px' }}>CONCEPTO</th>
                                                <th className="py-3" style={{ fontWeight: 500, color: '#5f6368', fontSize: '13px' }}>CONDUCTOR</th>
                                                <th className="py-3 text-center" style={{ fontWeight: 500, color: '#5f6368', fontSize: '13px' }}>ESTADO</th>
                                                <th className="py-3 px-3 text-end" style={{ fontWeight: 500, color: '#5f6368', fontSize: '13px' }}>IMPORTE</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {payments.map((payment) => (
                                                <tr
                                                    key={payment.id || `payment-${Math.random()}`}
                                                    onClick={() => navigate(`/payment/${payment.id}`)}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <td className="py-3 px-3">
                                                        <span className="text-muted" style={{ fontSize: '14px' }}>
                                                            {payment.updated ? moment(payment.updated).format('DD/MM/YYYY HH:mm') : '-'}
                                                        </span>
                                                    </td>
                                                    <td className="py-3">
                                                        <span style={{ color: '#202124' }}>{payment.concept || '-'}</span>
                                                    </td>
                                                    <td className="py-3">
                                                        {payment.collector ? (
                                                            <div className="d-flex align-items-center">
                                                                <div
                                                                    className="d-flex align-items-center justify-content-center rounded-circle me-2"
                                                                    style={{
                                                                        width: '28px',
                                                                        height: '28px',
                                                                        backgroundColor: '#e8f0fe',
                                                                        color: '#1a73e8',
                                                                        fontSize: '12px',
                                                                        fontWeight: 600
                                                                    }}
                                                                >
                                                                    {(payment.collector.name || '?').charAt(0).toUpperCase()}
                                                                </div>
                                                                <span style={{ color: '#202124' }}>{payment.collector.name}</span>
                                                            </div>
                                                        ) : '-'}
                                                    </td>
                                                    <td className="py-3 text-center">
                                                        <Badge
                                                            bg={payment.is_paid === 1 ? 'success' : 'warning'}
                                                            style={{ fontSize: '12px', padding: '6px 12px', fontWeight: 500 }}
                                                        >
                                                            {payment.is_paid === 1 ? 'Pagado' : 'Pendiente'}
                                                        </Badge>
                                                    </td>
                                                    <td className="py-3 px-3 text-end">
                                                        <span style={{ fontWeight: 600, color: '#202124' }}>
                                                            {typeof payment.price === 'number' ? `${payment.price.toFixed(2)} €` : '-'}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                )}
                            </Card.Body>
                        </Card>
                    </Tab.Pane>
                </Tab.Content>
            </Tab.Container>
        </div>
    );
}
